import { DollarSign, TrendingUp, Target } from "lucide-react";

export function ResultsValidationTab({ resultsSummary }) {
  if (!resultsSummary) return null;
  const { financial, baseline, control_chart, kpis, solutions_count } = resultsSummary;

  return (
    <div className="space-y-6">
      {/* Financial Impact */}
      <div className="bg-card border border-border rounded-lg p-6 space-y-4">
        <div className="flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-primary" />
          <h3 className="font-heading font-semibold">Financial Impact Summary</h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {financial.copq_current && (
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-copq-before">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">COPQ (Before)</p>
              <p className="text-2xl font-bold font-heading text-red-400">${Number(financial.copq_current).toLocaleString()}</p>
            </div>
          )}
          {financial.copq_target && (
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-copq-target">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">COPQ (Target)</p>
              <p className="text-2xl font-bold font-heading text-emerald-400">${Number(financial.copq_target).toLocaleString()}</p>
            </div>
          )}
          {financial.gross_savings && (
            <div className="bg-background border border-emerald-500/30 rounded-lg p-4" data-testid="rv-savings">
              <p className="text-xs text-emerald-400 uppercase tracking-wider font-medium">Gross Savings</p>
              <p className="text-2xl font-bold font-heading text-emerald-400">${Number(financial.gross_savings).toLocaleString()}</p>
            </div>
          )}
          {financial.net_benefit != null && (
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-net-benefit">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Net Benefit</p>
              <p className={`text-2xl font-bold font-heading ${Number(financial.net_benefit) >= 0 ? "text-emerald-400" : "text-red-400"}`}>${Number(financial.net_benefit).toLocaleString()}</p>
            </div>
          )}
          {financial.roi_percent != null && (
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-roi">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">ROI</p>
              <p className="text-2xl font-bold font-heading">{Number(financial.roi_percent).toFixed(1)}%</p>
            </div>
          )}
          {financial.implementation_cost && (
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-impl-cost">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Implementation Cost</p>
              <p className="text-2xl font-bold font-heading">${Number(financial.implementation_cost).toLocaleString()}</p>
            </div>
          )}
        </div>
      </div>

      {/* Baseline vs Current */}
      {baseline.column && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">Baseline vs Current Performance</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-baseline-column">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Metric</p>
              <p className="text-lg font-bold font-heading">{baseline.column}</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-baseline-mean">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Baseline Mean</p>
              <p className="text-lg font-bold font-heading">{baseline.mean}</p>
            </div>
            <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-baseline-std">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Baseline Std</p>
              <p className="text-lg font-bold font-heading">{baseline.std}</p>
            </div>
            {control_chart.mean && (
              <div className="bg-background border border-border rounded-lg p-4" data-testid="rv-current-mean">
                <p className="text-xs text-muted-foreground uppercase tracking-wider font-medium">Control Chart Mean</p>
                <p className="text-lg font-bold font-heading">{control_chart.mean}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* KPIs */}
      {kpis.length > 0 && (
        <div className="bg-card border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            <h3 className="font-heading font-semibold">KPI Summary</h3>
          </div>
          <div className="space-y-3">
            {kpis.map((kpi, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-background border border-border rounded-lg" data-testid={`rv-kpi-${i}`}>
                <span className="text-sm font-medium">{kpi.name}</span>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-muted-foreground">Current: <strong>{kpi.current ?? "N/A"}</strong></span>
                  <span className="text-muted-foreground">Target: <strong>{kpi.target ?? "N/A"}</strong></span>
                  {kpi.unit && <span className="text-xs text-muted-foreground">{kpi.unit}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-lg p-4 text-center" data-testid="rv-solutions-count">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">Solutions Implemented</p>
          <p className="text-3xl font-bold font-heading">{solutions_count}</p>
        </div>
        <div className="bg-card border border-border rounded-lg p-4 text-center" data-testid="rv-kpis-count">
          <p className="text-xs text-muted-foreground uppercase tracking-wider">KPIs Tracked</p>
          <p className="text-3xl font-bold font-heading">{kpis.length}</p>
        </div>
      </div>
    </div>
  );
}
